import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import "./styled-modal.style.css";

function StyledModalView({
    isOpen,
    onClose,
    title,
    children,
    confirmText,
    closeText,
    onConfirm
}) {
    const { t } = useTranslation();
    const modalRef = useRef(null);
    const previousFocusRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            // Store the element that had focus before modal opened
            previousFocusRef.current = document.activeElement;

            // Prevent body scroll
            document.body.style.overflow = 'hidden';

            // Focus the modal for accessibility
            if (modalRef.current) {
                modalRef.current.focus();
            }
        } else {
            // Restore body scroll
            document.body.style.overflow = '';

            // Return focus to previous element
            if (previousFocusRef.current) {
                previousFocusRef.current.focus();
            }
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    // Handle escape key
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <div
            className="modal-overlay"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
        >
            <div
                className="modal-content card"
                onClick={(e) => e.stopPropagation()}
                ref={modalRef}
                tabIndex={-1}
            >
                <header className="modal-header card-header">
                    <h2 id="modal-title" className="card-title text-xl">
                        {title}
                    </h2>
                    <button
                        type="button"
                        className="modal-close btn-close"
                        onClick={onClose}
                        aria-label={t('common.close')}
                    >
                        ×
                    </button>
                </header>

                <div className="modal-body card-body">
                    {children}
                </div>

                {(confirmText || closeText) && (
                    <footer className="modal-footer card-footer">
                        {closeText && (
                            <button
                                type="button"
                                className="btn btn-cancel"
                                onClick={onClose}
                            >
                                {closeText}
                            </button>
                        )}
                        {confirmText && (
                            <button
                                type="button"
                                className="btn btn-accept"
                                onClick={onConfirm}
                            >
                                {confirmText}
                            </button>
                        )}
                    </footer>
                )}
            </div>
        </div>
    );
}

StyledModalView.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    confirmText: PropTypes.string,
    closeText: PropTypes.string,
    onConfirm: PropTypes.func
};

StyledModalView.defaultProps = {
    confirmText: null,
    closeText: null,
    onConfirm: null
};

function StyledModalContainer(props) {
    // No data logic yet, but this is ready for future extension
    return <StyledModalView {...props} />;
}

export default StyledModalContainer;
